import { api, type AppearanceRow } from '@setplex/tria-api'
import { createEvent, sample, attach } from 'effector'
import { aye, nay } from '~/shared/helpers'
import { logger } from '~/shared/logger'
import {
  Awaiting,
  Failed,
  Loading,
  Ready,
  Updating,
  isType,
  toDirty,
  toLoading,
} from './lib'
import { appearanceFeature } from '~/features/appearance'

import { $currentRows, $loadRowQueue, leaveModule } from './model'

const $identifier = appearanceFeature.$currentIdentifier

export const getRowContentFx = attach({
  effect: api.appearance.getRowContentFx,
  mapParams: (params: {
    appearance: AppearanceRow
    identifier: string | null
  }) => params.appearance,
})

export const resetRowsContent = createEvent()

export const dirty = createEvent()

sample({
  clock: leaveModule,
  target: dirty,
})

// mark empty rows as loading, and dirty rows as updating
sample({
  clock: getRowContentFx,
  source: $currentRows,
  filter: (currentRows) => aye(currentRows),
  fn: (currentRows, { appearance: { id } }) => {
    if (!currentRows) return currentRows

    const row = currentRows.get(id)
    if (!row) return currentRows

    const newRows = new Map(currentRows)
    return newRows.set(id, toLoading(row))
  },
  target: $currentRows,
  batch: false,
})

// get loaded row data and update row in the list
// skip updating if row's state was changed from loading|updating (for example user left the page)
sample({
  clock: getRowContentFx.done,
  source: {
    currentRows: $currentRows,
    identifier: $identifier,
  },
  filter: ({ currentRows, identifier }, { params }) => {
    const { id } = params.appearance

    if (!identifier || identifier !== params.identifier || !currentRows)
      return false

    const data = currentRows.get(id)?.data
    return isType(data, Loading) || isType(data, Updating)
  },
  fn: ({ currentRows }, { params, result: { items, last } }) => {
    const { id } = params.appearance
    const row = currentRows!.get(id)

    if (!row) return currentRows
    const { desc } = row

    const updated = new Map(currentRows!)

    // Update to Awaiting
    updated.set(id, {
      desc,
      data: Awaiting(items),
      hasMore: !last,
    })

    // Awaiting -> Ready for all rows
    for (const [key, value] of updated) {
      const { data, hasMore, desc } = value
      if (isType(data, Awaiting)) {
        updated.set(key, {
          desc,
          data: Ready(data.payload),
          hasMore,
        })
      }
    }

    return updated
  },
  target: $currentRows,
})

// update failed row content in the list
// skip updating if row's state was changed from loading|updating (for example user left the page)
sample({
  clock: getRowContentFx.fail,
  source: $currentRows,
  filter: (currentRows, { params }) => {
    const { id } = params.appearance

    if (nay(currentRows)) return false

    const data = currentRows!.get(id)?.data
    return isType(data, Loading) || isType(data, Updating)
  },
  fn: (currentRows, { params, error }) => {
    const { id } = params.appearance

    const row = currentRows!.get(id)

    if (!row) return currentRows

    const { desc, hasMore } = row

    return new Map(currentRows).set(id, {
      desc,
      hasMore,
      data: Failed(error),
    })
  },
  target: $currentRows,
})

// mark refreshable rows as dirty (or reset them to empty, if they are not ready)
sample({
  clock: dirty,
  source: $currentRows,
  filter: (currentRows) => aye(currentRows),
  fn: (currentRows) => {
    const updated = new Map()

    for (const [id, row] of currentRows!) updated.set(id, toDirty(row))

    return updated
  },
  target: $currentRows,
})

sample({
  clock: getRowContentFx.fail,
  fn: ({ params, error }) => ['failed to load row', params, ':', error],
  target: logger.errorFx,
})

const MAX_PARALLEL_LOAD = 3

sample({
  clock: getRowContentFx,
  source: $loadRowQueue,
  fn: (queue, { appearance: { id } }) =>
    queue.some((row) => row.id === id)
      ? queue.filter((row) => row.id !== id)
      : queue,
  target: $loadRowQueue,
})

// watch load queue and start loading rows if there are free slots for parallel loading
sample({
  clock: [$loadRowQueue, getRowContentFx.inFlight],
  source: {
    queue: $loadRowQueue,
    inFlight: getRowContentFx.inFlight,
    identifier: $identifier,
  },
  filter: ({ queue, inFlight }) =>
    queue.length > 0 && inFlight < MAX_PARALLEL_LOAD,
  fn: ({ queue, identifier }) => ({
    appearance: queue[0],
    identifier,
  }),
  target: getRowContentFx,
})
